import { Equal, MessageCircle } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { Pressable } from "react-native";
import { View, XStack, YStack } from "tamagui";
import { Body } from "../../../ui/Body";
import { Button } from "../../../ui/Button";
import { Group } from "../../../ui/Group";
import { Label } from "../../../ui/Label";
import { Separator } from "../../../ui/Separator";
import { Title } from "../../../ui/Title";
import { AvatarSize, UserAvatar } from "../../../ui/UserAvatar";
import type TrackerModuleModel from "../TrackerModuleModel";
import type { TrackerLeaderboardItem } from "../TrackerModuleModel";
import { TrackerHistoryListModal } from "./TrackerHistoryListModal";

const STEP_AMOUNT = 10;

function getLanguage() {
  if (navigator === undefined) {
    return "en-US";
  }

  if (navigator.languages !== undefined) {
    return navigator.languages[0];
  }

  return navigator?.language ?? "en-US";
}

export const TrackerLeaderboardRow: React.FC<{
  position: number;
  highlight?: boolean;
  entry: TrackerLeaderboardItem;
  module: TrackerModuleModel;
}> = observer(({ position, highlight, entry, module }) => {
  const [showModal, setShowModal] = React.useState(false);

  if (!entry.user) {
    return null;
  }

  const formatter = new Intl.NumberFormat(getLanguage(), {});

  return (
    <>
      <Pressable
        style={{ flex: 1, width: "100%" }}
        onPress={() => {
          setShowModal(true);
        }}
      >
        <XStack
          width="100%"
          gap="$medium"
          alignItems="center"
          justifyContent="space-between"
        >
          <View
            width={50}
            alignItems="center"
            justifyContent="center"
            backgroundColor={
              highlight ? "$pressablePrimaryBackground" : undefined
            }
            aspectRatio={1}
          >
            <Title color={highlight ? "$pressablePrimaryLabel" : undefined}>
              {position}
            </Title>
          </View>

          <UserAvatar userUid={entry.user.uid} size={AvatarSize.Medium} />
          <YStack gap="$small" flex={1}>
            <Label numberOfLines={1} ellipsizeMode="tail">
              {entry.user?.data.displayName}
            </Label>
            <XStack gap="$medium">
              <Body muted>{entry.totalEntries} entries</Body>
            </XStack>
            {!!entry.lastComment && (
              <XStack gap="$small" alignItems="center">
                <MessageCircle size={"$small"} color="$muted" />
                <Body fontStyle="italic">"{entry.lastComment}"</Body>
              </XStack>
            )}
          </YStack>

          <XStack gap="$small" alignItems="center">
            <Equal size={"$small"} />
            <Title testID="totalCount">
              {formatter.format(Math.round(entry.totalCount))}
            </Title>
          </XStack>
        </XStack>
      </Pressable>

      <TrackerHistoryListModal
        visible={showModal}
        onClose={() => setShowModal(false)}
        userUid={entry.user.uid}
        module={module}
      />
    </>
  );
});

export const TrackerLeaderboard: React.FC<{
  module: TrackerModuleModel;
}> = observer(({ module }) => {
  const leaderboardData = module.leaderboard;

  const [showAmount, setShowAmount] = React.useState(STEP_AMOUNT);

  if (!leaderboardData.length) {
    return null;
  }

  const currentUserUid = module.userUid;

  const items = leaderboardData.slice(0, showAmount);

  const currentUserIndex = leaderboardData.findIndex(
    (i) => i.user?.uid === currentUserUid,
  );

  const myExtraRow =
    currentUserIndex !== undefined && currentUserIndex + 1 > showAmount
      ? leaderboardData[currentUserIndex]
      : null;

  const showMoreButton = showAmount < leaderboardData.length;

  const itemsLeftLabel: string = `Show more (${
    leaderboardData.length - items.length
  })`;

  return (
    <>
      <Group>
        {items.map((t, i) => (
          <TrackerLeaderboardRow
            key={t.user?.uid}
            entry={t}
            position={i + 1}
            highlight={t.user?.uid === currentUserUid}
            module={module}
          />
        ))}
        {myExtraRow && (
          <>
            <Separator noMargins />
            <TrackerLeaderboardRow
              entry={myExtraRow}
              position={currentUserIndex + 1}
              highlight={true}
              module={module}
            />
          </>
        )}
      </Group>
      {showMoreButton && (
        <View marginTop="$medium">
          <Button
            type="secondary"
            onPress={() => {
              setShowAmount(showAmount + STEP_AMOUNT);
            }}
          >
            {itemsLeftLabel}
          </Button>
        </View>
      )}
    </>
  );
});
